import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { formatPercent } from "@/features/matches";

import { formatBuyTime } from "../../lib/formatBuyTime";
import type { HeroItem, ItemPurposeTag } from "../../model/HeroBuilds";
import { ITEM_PURPOSE_LABEL } from "../../model/ITEM_PURPOSE_LABEL";
import { SITUATIONAL_GROUPS } from "../../model/SITUATIONAL_GROUPS";
import { HERO_ITEM_FOOTER } from "../../model/HERO_ITEM_FOOTER";
import { BuildItems } from "../BuildItems";

const TITLE = msg({ id: "builds.overview.situational", message: "Situational" });
const HINT = msg({
  id: "builds.overview.situational_hint",
  message: "15 to 60% of players take these, check your match",
});

type SituationalGroupsProps = {
  items: readonly HeroItem[];
  purposes: readonly ItemPurposeTag[];
  hero: string;
};

// Предметы «по ситуации» (доска «19д»): по группам — зачем их берут, — с подписью в пару слов
// под иконкой; минута и доля — в описании. Группы и подписи — коды с бэкенда.
export const SituationalGroups = ({ items, purposes, hero }: SituationalGroupsProps) => {
  const { _, i18n } = useLingui();
  const tags = new Map(purposes.map((tag) => [tag.item_id, tag]));
  const groups = SITUATIONAL_GROUPS.map((group) => ({
    ...group,
    items: items.filter((item) => (tags.get(item.item_id)?.group ?? "damage") === group.group),
  })).filter((group) => group.items.length > 0);
  if (groups.length === 0) {
    return null;
  }
  return (
    <section aria-label={_(TITLE)} className="@container flex flex-col gap-3">
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h4 className="text-base font-semibold">{_(TITLE)}</h4>
        <span className="text-[13px] text-subtle">{_(HINT)}</span>
      </div>
      <div className="grid gap-3 @2xl:grid-cols-2">
        {groups.map((group) => {
          const title = _(group.title);
          return (
            <section
              key={group.group}
              aria-label={title}
              className="flex flex-col gap-2.5 rounded-xl border border-border bg-surface px-3.5 py-3"
            >
              <div className="flex flex-col">
                <h5 className="text-[15px] font-semibold">{title}</h5>
                <span className="text-[13px] text-subtle">{_(group.when)}</span>
              </div>
              <BuildItems
                size={48}
                width={76}
                showPickRate={false}
                className="gap-x-2 gap-y-2.5"
                items={group.items.map((item) => {
                  const purpose = tags.get(item.item_id)?.purpose;
                  const caption = purpose == null ? undefined : _(ITEM_PURPOSE_LABEL[purpose]);
                  return {
                    itemId: item.item_id,
                    atS: item.avg_buy_time_s,
                    hideTime: true,
                    caption,
                    why: caption === undefined ? title : `${title} · ${caption}`,
                    footer: _({
                      ...HERO_ITEM_FOOTER,
                      values: {
                        share: formatPercent(item.pick_rate, i18n.locale),
                        hero,
                        time: formatBuyTime(item.avg_buy_time_s),
                      },
                    }),
                  };
                })}
              />
            </section>
          );
        })}
      </div>
    </section>
  );
};
